import type { AudioMixerState, Campaign, FogState, MapGeometry, ProjectionAwarenessSettings, Scene, WorkspaceState } from "../types";
import { AIDM_NARRATOR_PROFILE_VERSION, DEFAULT_AIDM_COST_MODE, DEFAULT_AIDM_LANGUAGE, DEFAULT_AIDM_MODEL, DEFAULT_AIDM_VOICE } from "./aidm";
import { DEFAULT_COVER_COLOR, DEFAULT_DM_TABLE_EDGE } from "./table-options";
import { defaultTokenGridSnapping } from "./token-grid";
import { createPokerState } from "./poker";
import { createBroadsideState } from "./broadside";
import { randomUuid } from "./browser-crypto";

// Retained so audio already assigned to the global library remains attached after the rebrand.
export const GLOBAL_MEDIA_CAMPAIGN_ID = "__jftt_global_media__";

export const defaultProjectionAwareness = (): ProjectionAwarenessSettings => ({
  enabled: true,
  projectorEdge: "overhead",
  safetyMarginInches: 0.08,
  spillPerHeight: 0.12,
});

export const defaultAudioMixer = (): AudioMixerState => ({
  masterVolume: 1,
  categoryVolumes: { ambience: 1, sfx: 1, music: 1 },
  tracks: {},
});

export const uid = (prefix = "id") => `${prefix}_${randomUuid()}`;

export const emptyFog = (): FogState => ({
  operations: [],
  undoCursor: 0,
  explored: [],
  allowAll: false,
});

export const fogNeedsReset = (fog: FogState): boolean => Boolean(
  fog.allowAll || fog.operations.length || fog.explored.length || fog.exploredMaskRef,
);

export const generatedGeometry = (): MapGeometry => ({
  width: 24,
  height: 16,
  grid: { size: 1, type: "square", color: "#9f9072", opacity: 0.28, offset: { x: 0, y: 0 } },
  walls: [],
  objectWalls: [],
  portals: [],
  lights: [],
  ambientLight: 0.55,
  source: { format: "generated", warnings: [] },
});

export function createMapScene(campaignId: string, name = "Table Map"): Scene {
  const now = Date.now();
  return {
    id: uid("scene"), campaignId, name, type: "map", background: "#26231d", fit: "contain",
    view: { x: 0, y: 0, zoom: 1, rotation: 0, mode: "fit" }, gridInches: 1,
    transitionMs: 240, timeOfDay: "afternoon", atmosphericFog: "off", rain: "off", lightning: "off", snow: "off", snowflakeSize: "mixed", windSpeed: "normal", waterRegions: [], weatherDryRegions: [], materialRegions: [], physicalObjects: [], environmentSounds: {}, geometry: generatedGeometry(), tokens: [], fog: emptyFog(),
    createdAt: now, updatedAt: now,
  };
}

export function createPokerScene(campaignId: string, name = "Poker Table"): Scene {
  const now = Date.now();
  return {
    id: uid("scene"), campaignId, name, type: "poker", background: "#07150f", fit: "contain",
    view: { x: 0, y: 0, zoom: 1, rotation: 0, mode: "fit" }, gridInches: 1, transitionMs: 240,
    tokens: [], fog: emptyFog(), poker: createPokerState(now), createdAt: now, updatedAt: now,
  };
}

export function createBroadsideScene(campaignId: string, name = "Broadside Skirmish"): Scene {
  const now = Date.now();
  return {
    id: uid("scene"), campaignId, name, type: "broadside", background: "#041923", fit: "contain",
    view: { x: 0, y: 0, zoom: 1, rotation: 0, mode: "fit" }, gridInches: 1, transitionMs: 240,
    tokens: [], fog: emptyFog(), broadside: createBroadsideState(now), createdAt: now, updatedAt: now,
  };
}

export function createWorkspace(): WorkspaceState {
  const now = Date.now();
  const campaign: Campaign = { id: uid("campaign"), name: "My Campaign", createdAt: now, updatedAt: now };
  const scene = createMapScene(campaign.id, "Opening Map");
  campaign.activeSceneId = scene.id;
  return {
    schemaVersion: 2,
    campaigns: [campaign], heroes: [], assets: [], scenes: [scene], calibrations: [],
    presentation: {
      revision: 0, campaignId: campaign.id, activeSceneId: scene.id, stagedReady: false,
      blackout: false, covered: false, sessionWaiting: false, displayConnected: false, lastUpdated: now,
    },
    settings: { storagePersistent: false, activeCampaignId: campaign.id, audioMixer: defaultAudioMixer(), dmTableEdge: DEFAULT_DM_TABLE_EDGE, coverColor: DEFAULT_COVER_COLOR, sceneFolders: [], tokenPresets: [], audioFolders: [], audioPlaylists: [], cueSequences: [], aidmModel: DEFAULT_AIDM_MODEL, aidmLanguage: DEFAULT_AIDM_LANGUAGE, aidmCostMode: DEFAULT_AIDM_COST_MODE, aidmVoice: DEFAULT_AIDM_VOICE, aidmAutoNarrate: false, aidmNarratorProfileVersion: AIDM_NARRATOR_PROFILE_VERSION, projectionAwareness: defaultProjectionAwareness(), tokenGridSnapping: defaultTokenGridSnapping(), playableVideoAutoplay: false },
  };
}
